import React, {FunctionComponent, useEffect, useState} from 'react'
import styles from './TaskEdit.module.sass'
import Switch from '../../../../../controls/Switch/Switch'
import DateInput from '../../../../../data-components/date/DateInput/DateInput'
import PriorityPicker from '../../../../../data-components/pickers/PriorityPicker/PriorityPicker'
import Button from '../../../../../controls/Button/Button'
import {TenderStatus} from '../../../../../data-components/pickers/TenderPicker/TenderPicker'
import {PartialTask, Task} from '../../../../../../responses/tasks'
import {entityRemove, getId} from '../../../../../../utils/entity-utils'
import {parseDate, toDateTime, toServerDate} from '../../../../../../utils/date-utils'
import {User} from '../../../../../../responses/users'
import UserPanel from '../../../../../data-components/elements/UserElement/UserElement'
import {flow} from 'fp-ts/es6/function'
import List from '../../../../../data-components/lists/List/List'
import clsx from 'clsx'
import {TaskStatus} from '../../../../../../constants/enums'
import UserPicker from '../../../../../data-components/pickers/UserPicker/UserPicker'
import {ID} from '../../../../../../types/UtilityTypes'
import {useSetState} from 'react-use'
import {defaultValue, isDefined, modifyProp, unboxRecord, unit} from '../../../../../../utils/utils'
import {useAllPermissions, useRootSelector, useUpload} from '../../../../../../utils/hooks'
import {RequestStatus} from '../../../../../../responses/response'
import {motion} from 'framer-motion'
import OkCancel from '../../../../../controls/OkCancel/OkCancel'
import DeleteIcon from '../../../../../../../files/delete.svg'
import {apiRequests} from '../../../../../../http/config'
import {fromQueryWithReset, ResetSearch} from '../../../../../../utils/rx-operators'
import {useQueries} from '../../../../../../utils/hooks/useQueries'
import DateDialog from '../../../../../data-components/date/DateDialog/DateDialog'
import {PERMISSIONS} from '../../../../../../constants/permissions'
import CommentElement from '../../../../../data-components/elements/CommentElement/CommentElement'
import {useObservableState} from 'observable-hooks'
import {TaskType} from '../../../../../../responses/refs/refs'
import Dropdown from '../../../../../data-components/dropdowns/Dropdown/Dropdown'
import {Observable, of} from 'rxjs'
import {switchMap} from 'rxjs/operators'
import {EMPTY_PROJECT, Project} from '../../../../../../responses/projects'
import {projectSelector} from '../../../../../../utils/selectors'
import TaskChecklist from '../TaskChecklist/TaskChecklist'
import QuillEditor from '../../../../../etc/Editor/QuillEditor'
import {isTempFileData} from '../../../../../../responses/files'

interface Props {
    className?: string,
    onSuccess?: (task: PartialTask) => void,
    onCancel?: () => void,
    projectId: ID,
    task?: Task,
    type_id?: ID,
    actionText: string,
}


const TaskEdit: FunctionComponent<Props> = props => {
    const [task, setTask] = useSetState<PartialTask>({
        priority_id: 1,
    })
    const project: Project = useRootSelector(projectSelector(props.projectId)) || EMPTY_PROJECT
    const updateStatus = useRootSelector(state => state.data.task.updateStatus)
    const canOpen = useAllPermissions([PERMISSIONS.TASK.OPEN])
    const [text, setText] = useState('')
    const upload = useUpload(task.comment?.files)

    const {isMobile} = useQueries()

    useEffect(() => {
        props.task && setTask(props.task)
    }, [props.task, setTask])

    const getUsers = (data: Observable<{ query: ResetSearch, taskTypeID?: ID }>) => data.pipe(
        switchMap(({query, taskTypeID}) => of(query).pipe(
            fromQueryWithReset(query => apiRequests.project.targetUsersNewTask({
                params: {projectId: props.projectId}, query: {...query, task_type: taskTypeID}
            }))
        ))
    )

    const [users, searchUsers] = useObservableState(getUsers, [])

    const [types, searchTypes] = useObservableState<TaskType[], { search: string, reset: boolean }>(fromQueryWithReset(
        query => apiRequests.dictionary.list({params: {key: 'task-type'}, query})
    ), [])

    return <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1, position: 'static'}}
        exit={{opacity: 0, position: 'absolute', top: 0}}
        className={clsx(props.className, styles.taskEdit)}
    >
        {
            props.task && props.task.status_id === TaskStatus.REQUESTED && <div className={styles.header}>
                <div className={styles.left}>
                    <div className={styles.title}>Запрос на открытие задачи</div>
                    {
                        props.task.details.comment && <CommentElement comment={props.task.details.comment}/>
                    }
                </div>
                <div className={styles.info}>
                    <div className={styles.username}>{props.task.details.user?.name}</div>
                    <div className={styles.date}>{flow(parseDate, toDateTime)(props.task.details.created_at)}</div>
                </div>
            </div>
        }
        <div className={styles.content}>
            <Dropdown
                className={styles.type}
                data={types}
                content={(selected, render) => selected.map(render).join()}
                placeholder={'Тип задач'}
                toKey={getId}
                selected={unit(task.type)}
                render={type => type.name}
                onClick={type => {
                    setTask({
                        type,
                        target_user: type?.executors && type?.executors.length > 0
                            ? type?.executors?.filter(item => item.work_type?.id === project.work_type?.id).length > 0
                                ? type?.executors?.filter(item => item.work_type?.id === project.work_type?.id)[0].user
                                : undefined
                            : undefined,
                        type_id: type.id
                    })
                }}
                onChange={(search, reset) => {
                    searchTypes({search, reset})
                }
                }
            />
            <UserPicker
                className={styles.user}
                data={users}
                disabled={task.type ? false : true}
                text={'Добавить исполнителя'}
                title={'Изменить исполнителя'}
                user={task.target_user}
                onClick={target_user => setTask({target_user})}
                onChange={(search, reset) => {
                    if (search.length < 1) reset = true
                    searchUsers({query: {search, reset}, taskTypeID: task.type?.id})
                }}
            />
            {
                isMobile ? <DateDialog
                    className={styles.deadline}
                    text={'Дедлайн'}
                    disabledDays={{before: new Date()}}
                    value={parseDate(task.deadline_at)}
                    onChange={deadline_at => setTask({deadline_at: toServerDate(deadline_at)})}
                /> : <DateInput
                    className={styles.deadline}
                    placeholder={'Дедлайн'}
                    disabledDays={{before: new Date()}}
                    value={parseDate(task.deadline_at)}
                    onChange={deadline_at => setTask({deadline_at: toServerDate(deadline_at)})}
                />
            }
            <PriorityPicker
                disabled={!canOpen}
                className={styles.priority}
                priority={task.priority_id || 1}
                onChange={priority_id => setTask({priority_id})}
            />
            <Switch
                className={styles.tender}
                text={'Тендер'}
                value={isDefined(task.tender_id) && task.tender_id !== TenderStatus.CANCELED}
                onChange={value => setTask({tender_id: value ? TenderStatus.IN_WORK : TenderStatus.CANCELED})}
            />
            {
                !isMobile && <div className={styles.users}>
                    <List<User>
                        className={styles.list}
                        data={task.users || []}
                        toKey={getId}
                        toClassName={() => styles.listItem}
                        render={user => <>
                            <UserPanel className={styles.listUser} disabled user={user}/>
                            <Button
                                className={styles.button}
                                onClick={() => setTask(modifyProp('users', flow(defaultValue<User[]>([]), entityRemove(user))))}
                            >
                                <DeleteIcon/>
                            </Button>
                        </>}
                    />
                </div>
            }
            {
                task.type?.checklist && <TaskChecklist className={styles.checklist} content={task.type.checklist}/>
            }
            {
                task.type?.checklist && <QuillEditor
                    className={styles.editor}
                    placeholder={'Добавить чек-лист'}
                    value={text}
                    onChange={setText}
                    upload={upload}
                />
            }
            <OkCancel
                className={styles.actions}
                disabled={updateStatus === RequestStatus.FETCHING || !task.deadline_at}
                onCancel={props.onCancel}
                title={{
                    content: 'Дедлайн не заполнен',
                    disable: !!task.deadline_at,
                }}
                onOk={() => props.onSuccess?.({
                    ...task,
                    comment: unboxRecord({
                        text,
                        files: upload.files.filter(isTempFileData).map(file => file.data),
                    })
                })}
                text={props.actionText}
            />
        </div>
    </motion.div>
}

export default TaskEdit
